import type {
  ChatPagingRequest,
  ConsultationChatMessageResponse,
  ConsultationDetailResponse,
  ConsultationListItem,
  ActiveConsultationResponse,
  MyStepItemResponse,
  StreamingChunk,
  UserConsultBlockItemResponse,
  NoneStreamingChatResponse,
  RecommendationResponse,
  FinishConsultationBody,
  ConversationReportStatusResponse
} from './model/chatType'

import { jwtClientAxios } from '@/api'
import api from '@/api/url'
import { useAuthStore } from '@/store/authStore'

/* 유저 상담 블럭 리스트 조회 (채팅 홈 페이지 ) */
export const getUserConsultBlockListApi = async (user_id: number): Promise<UserConsultBlockItemResponse[] | null> => {
  const response = await jwtClientAxios().get(api.userConsultBlock.GET_USER_CONSULT_BLOCK_LIST(user_id))
  return response.data.data
}

/* 공통 스트리밍 처리 함수 */
const handleStreamingResponse = async (
  response: Response,
  onChunk: (chunk: StreamingChunk) => void,
  onComplete: (consultationId?: number) => void,
  _onError: (error: Error) => void
): Promise<void> => {
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }

  if (!response.body) {
    throw new Error('Response body is null')
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()

  try {
    while (true) {
      const { done, value } = await reader.read()

      if (done) {
        onComplete()
        break
      }

      // 받은 chunk를 텍스트로 디코딩
      const chunk = decoder.decode(value, { stream: true })

      // SSE 라인별로 처리
      const lines = chunk.split('\n')

      for (const line of lines) {
        if (line.trim() && line.startsWith('data: ')) {
          try {
            const jsonData = line.replace('data: ', '').trim()
            if (jsonData) {
              const parsedData: StreamingChunk = JSON.parse(jsonData)

              onChunk(parsedData)

              // consultation_response_completed이면 완료 처리
              if (parsedData.type === 'consultation_response_completed') {
                onComplete(parsedData.consultation_id)
                return
              }
            }
          } catch (parseError) {
            console.warn('JSON 파싱 에러:', parseError, 'Raw data:', line)
          }
        }
      }
    }
  } finally {
    reader.releaseLock()
  }
}

/* 자유 상담 시작 (메시지 전송과 동시에 채팅방 생성. 스트리밍 방식) */
export const createNewChatApi = async (
  onChunk: (chunk: StreamingChunk) => void,
  onComplete: (consultationId?: number) => void,
  onError: (error: Error) => void,
  message?: string,
  block_id?: number,
  signal?: AbortSignal
): Promise<void> => {
  const timeoutId = setTimeout(() => {
    onError(new Error('요청 시간이 초과되었습니다. 다시 시도해주세요.'))
  }, 120000) // 120초 타임아웃

  const token = useAuthStore.getState().getAccessToken()

  try {
    const response = await fetch(api.chat.CREATE_NEW_CHAT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        Accept: 'text/event-stream' // SSE 형태로 받기
      },
      body: JSON.stringify({ message, block_id }),
      signal
    })

    await handleStreamingResponse(response, onChunk, onComplete, onError)
  } catch (error) {
    onError(error instanceof Error ? error : new Error('Unknown error'))
  } finally {
    clearTimeout(timeoutId)
  }
}

/* 상담 계속하기 (스트리밍 방식) */
export const continueConsultationApi = async (
  consultation_id: number,
  message: string,
  onChunk: (chunk: StreamingChunk) => void,
  onComplete: (consultationId?: number) => void,
  onError: (error: Error) => void,
  signal?: AbortSignal
): Promise<void> => {
  const timeoutId = setTimeout(() => {
    onError(new Error('요청 시간이 초과되었습니다. 다시 시도해주세요.'))
  }, 120000) // 120초 타임아웃

  try {
    const token = useAuthStore.getState().getAccessToken()
    const response = await fetch(api.chat.CONTINUE_CONSULTATION(consultation_id), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        Accept: 'text/event-stream' // SSE 형태로 받기
      },
      body: JSON.stringify({ message }),
      signal
    })

    await handleStreamingResponse(response, onChunk, onComplete, onError)
  } catch (error) {
    onError(error instanceof Error ? error : new Error('Unknown error'))
  } finally {
    clearTimeout(timeoutId)
  }
}

export const finishConsultationApi = async (consultation_id: number, body: FinishConsultationBody): Promise<void> => {
  try {
    const response = await jwtClientAxios().post(api.chat.FINISH_CONSULTATION(consultation_id), body)
    return response.data
  } catch (error) {
    console.error('상담 종료 실패:', error)
    throw error
  }
}

export const getConsultationListApi = async (pagingRequest: ChatPagingRequest): Promise<ConsultationListItem[]> => {
  try {
    const response = await jwtClientAxios().get(api.chat.GET_CONSULTATION_LIST, { params: pagingRequest })
    return response.data
  } catch (error) {
    console.error('상담 목록 조회 실패:', error)
    throw error
  }
}

export const getConsultationChatMessageListApi = async (
  consultation_id: number,
  pagingRequest: ChatPagingRequest
): Promise<ConsultationChatMessageResponse> => {
  try {
    const response = await jwtClientAxios().get(api.chat.GET_CONSULTATION_CHAT_MESSAGE_LIST(consultation_id), {
      params: pagingRequest
    })
    return response.data
  } catch (error) {
    console.error('상담 메시지 목록 조회 실패:', error)
    throw error
  }
}

export const editConsultationTitle = async (consultation_id: number, title: string): Promise<void> => {
  try {
    const response = await jwtClientAxios().patch(api.chat.EDIT_CONSULTATION_TITLE(consultation_id), { title })
    return response.data
  } catch (error) {
    console.error('상담 제목 수정 실패:', error)
    throw error
  }
}

export const deleteConsultationApi = async (consultation_id: number): Promise<void> => {
  try {
    const response = await jwtClientAxios().delete(api.chat.DELETE_CONSULTATION(consultation_id))
    return response.data
  } catch (error) {
    console.error('상담 삭제 실패:', error)
    throw error
  }
}

export const switchChitchatApi = async (consultation_id: number): Promise<void> => {
  try {
    const response = await jwtClientAxios().post(api.chat.SWITCH_CHITCHAT(consultation_id))
    return response.data.message
  } catch (error) {
    console.error('상담 주제 변경 실패:', error)
    throw error
  }
}

export const getMyStepsByLatestBlockApi = async (): Promise<MyStepItemResponse> => {
  try {
    const response = await jwtClientAxios().get(api.userConsultBlock.GET_MY_STEPS_BY_LATEST_BLOCK())
    return response.data
  } catch (error) {
    console.log(error)
    throw error
  }
}

export const getConsultationDetailApi = async (consultation_id: number): Promise<ConsultationDetailResponse> => {
  try {
    const response = await jwtClientAxios().get(api.chat.GET_CONSULTATION_DETAIL(consultation_id))
    return response.data
  } catch (error) {
    console.error('상담 상세 조회 실패:', error)
    throw error
  }
}

export const feedbackConsultationApi = async (
  consultation_id: number,
  rating: number,
  skip: boolean
): Promise<void> => {
  try {
    const response = await jwtClientAxios().post(api.chat.FEEDBACK_CONSULTATION(consultation_id), { rating, skip })
    return response.data
  } catch (error) {
    console.error('상담 피드백 실패:', error)
    throw error
  }
}

export const getActiveConsultationApi = async (): Promise<ActiveConsultationResponse> => {
  try {
    const response = await jwtClientAxios().get(api.chat.GET_ACTIVE_CONSULTATION())
    return response.data
  } catch (error) {
    console.error('상담 피드백 실패:', error)
    throw error
  }
}

// none streaming chat
export const getNoneStreamingChatApi = async (
  message: string,
  block_id: number
): Promise<NoneStreamingChatResponse> => {
  try {
    const response = await jwtClientAxios().post(api.chat.CREATE_NEW_CHAT, { message, block_id })
    return response.data
  } catch (error) {
    console.error('none streaming chat 실패:', error)
    throw error
  }
}

export const getNoneStreamingContinueConsultationApi = async (
  consultation_id: number,
  message: string
): Promise<NoneStreamingChatResponse> => {
  try {
    const response = await jwtClientAxios().post(api.chat.CONTINUE_CONSULTATION(consultation_id), { message })
    return response.data
  } catch (error) {
    console.error('none streaming continue consultation 실패:', error)
    throw error
  }
}

// 사용자 응답 추천
export const getRecommendationResponses = async (consultation_id: number): Promise<RecommendationResponse> => {
  try {
    const response = await jwtClientAxios().get(api.chat.GET_RECOMMENDATION_RESPONSE(consultation_id))
    return response.data
  } catch (error) {
    console.error(error)
    throw error
  }
}

// 인수인계 상태 확인 api
export const getTakeoverNoteStatus = async (block_id: number): Promise<{ proceed: boolean }> => {
  try {
    const response = await jwtClientAxios().get(api.chat.GET_TAKEOVER_NOTE_STATUS(block_id))
    return response.data
  } catch (error) {
    console.error(error)
    throw error
  }
}

// 특정 상담의 리포트 상태 조회
export const getConversationReportStatus = async (
  consultation_id: number
): Promise<ConversationReportStatusResponse> => {
  try {
    const response = await jwtClientAxios().get(api.chat.GET_CONVERSATION_REPORT_STATUS(consultation_id))
    return response.data
  } catch (error) {
    console.error(error)
    throw error
  }
}
